import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react'
import './mediaShelfDisplayCard.css'

export type MediaShelfDisplayCardProps = {
  /** Image / thumbnail / preview URL to render full-bleed inside the card. */
  src?: string
  /** Filename shown in the hover overlay and used as aria fallback. */
  name: string
  /** Optional alt text; defaults to `name`. */
  alt?: string
  /**
   * Chip anchored top-left on the slate. Only the icon is rendered by default —
   * use `chipLabel` if the caller ever wants text next to the icon.
   */
  sourceIcon?: ReactNode
  chipLabel?: ReactNode
  /**
   * Fallback used when `src` fails to load or is absent (e.g. a big file-type
   * glyph for non-image formats).
   */
  fallback?: ReactNode
  /**
   * Optional pre-measured aspect ratio (width / height). If omitted the card
   * measures the image once it loads and morphs accordingly. Clamped to the
   * range [3/4, 4/3] — anything outside letterboxes against a blurred backdrop.
   */
  aspectRatio?: number
  interactive?: boolean
  onClick?: () => void
  disabled?: boolean
  /** Adds `is-selected` styling for grid-selection states. */
  selected?: boolean
  className?: string
  style?: CSSProperties
  ariaLabel?: string
}

const MIN_ASPECT = 3 / 4
const MAX_ASPECT = 4 / 3
const EPSILON = 0.01

const clampAspect = (ratio: number): number => {
  if (!Number.isFinite(ratio) || ratio <= 0) return 1
  if (ratio < MIN_ASPECT) return MIN_ASPECT
  if (ratio > MAX_ASPECT) return MAX_ASPECT
  return ratio
}

const isLetterboxed = (natural: number, clamped: number): boolean =>
  Math.abs(natural - clamped) > EPSILON

export const MediaShelfDisplayCard = forwardRef<HTMLDivElement, MediaShelfDisplayCardProps>(
  function MediaShelfDisplayCard({
    src,
    name,
    alt,
    sourceIcon,
    chipLabel,
    fallback,
    aspectRatio,
    interactive,
    onClick,
    disabled = false,
    selected = false,
    className,
    style,
    ariaLabel,
  }: MediaShelfDisplayCardProps, ref) {
    const [naturalAspect, setNaturalAspect] = useState<number | null>(
      typeof aspectRatio === 'number' && aspectRatio > 0 ? aspectRatio : null,
    )
    const [hasError, setHasError] = useState(false)
    const imgRef = useRef<HTMLImageElement>(null)

    const handleLoad = useCallback(() => {
      const el = imgRef.current
      if (!el || !el.naturalWidth || !el.naturalHeight) return
      setNaturalAspect(el.naturalWidth / el.naturalHeight)
      setHasError(false)
    }, [])

    const handleError = useCallback(() => {
      setHasError(true)
    }, [])

    // Reset measurement when src changes.
    useEffect(() => {
      if (typeof aspectRatio === 'number' && aspectRatio > 0) {
        setNaturalAspect(aspectRatio)
        setHasError(false)
        return
      }
      setNaturalAspect(null)
      setHasError(false)
    }, [src, aspectRatio])

    const clampedAspect = useMemo(
      () => (naturalAspect != null ? clampAspect(naturalAspect) : 1),
      [naturalAspect],
    )
    const letterboxed = naturalAspect != null && isLetterboxed(naturalAspect, clampedAspect)
    const isInteractive = Boolean((interactive || onClick) && !disabled)
    const showImage = Boolean(src) && !hasError

    const cardStyle: CSSProperties & Record<string, string | number | undefined> = {
      ...style,
      '--card-aspect': `${clampedAspect}`,
    }
    if (letterboxed && showImage && src) {
      cardStyle['--card-media-backdrop'] = `url("${src}")`
    }

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      onClick?.()
    }
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isInteractive) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onClick?.()
    }

    const classNames = [
      'mediaShelfCard',
      isInteractive ? 'is-interactive' : '',
      selected ? 'is-selected' : '',
      disabled ? 'is-disabled' : '',
      letterboxed ? 'is-letterboxed' : '',
      showImage ? '' : 'is-empty',
      className ?? '',
    ].filter(Boolean).join(' ')

    return (
      <div
        ref={ref as Ref<HTMLDivElement>}
        className={classNames}
        style={cardStyle}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={ariaLabel ?? name}
        aria-disabled={disabled || undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        title={name}
      >
        <div className="mediaShelfCard__slate" aria-hidden={showImage ? undefined : true}>
          {letterboxed && showImage ? <div className="mediaShelfCard__backdrop" aria-hidden /> : null}
          {showImage ? (
            <img
              ref={imgRef}
              className="mediaShelfCard__image"
              src={src}
              alt={alt ?? name}
              loading="lazy"
              draggable={false}
              onLoad={handleLoad}
              onError={handleError}
            />
          ) : (
            <div className="mediaShelfCard__fallback" role="img" aria-label={alt ?? name}>
              {fallback}
            </div>
          )}
        </div>

        {sourceIcon || chipLabel ? (
          <span className="mediaShelfCard__chip" aria-hidden={chipLabel ? undefined : true}>
            {sourceIcon ? <span className="mediaShelfCard__chipIcon">{sourceIcon}</span> : null}
            {chipLabel ? <span className="mediaShelfCard__chipLabel">{chipLabel}</span> : null}
          </span>
        ) : null}

        <div className="mediaShelfCard__nameOverlay" aria-hidden>
          <span className="mediaShelfCard__name">{name}</span>
        </div>
      </div>
    )
  },
)

export default MediaShelfDisplayCard
